export const permissionConstants = {
  ALL: "all",
  USERS: "users",
  BILLING: "billing",
  INTEGRATIONS: "integrations",
  OBJECTS: "objects",
  CHATS: "chats",
  CALL_SETTINGS: "call_settings",
  EMAIL_TEMPLATES: "email_templates",
  DEAL_PIPELINE: "deal_pipeline",
  SHARED_EMAILS: "shared_emails",
  SHARED_LINKEDIN: "shared_linkedin",
  IMPORT_ACCESS: "import_access",
  EXPORT_ACCESS: "export_access",
  CUSTOM_VIEWS: "custom_views",
  PROSPECT_LEADS: "prospect_leads",
  PROSPECT_COMPANIES: "prospect_companies",
  AI_SDR: "ai_sdr",
  SEQUENCES: "sequences",
  DIALER: "dialer",
  CONVERSATIONS: "conversations",
  MEETINGS: "meetings",
  EMAILS: "emails",
  CONTACTS: "contacts",
  COMPANIES: "companies",
  LEADS: "leads",
  DEALS: "deals",
  LISTS: "lists",
  ANALYTICS: "analytics",
  TASKS: "tasks",
  ALL_TASKS: "all_tasks",
  CAMPAIGNS_IN_ENGAGE: "campaigns_in_engage",
  AGENT_BUILDER: "agent_builder",
  WATERFALL: "waterfall",
  BULK_DELETE: "bulk_delete",
  WORKFLOWS: "workflows",
  HR_ATTENDANCE: "attendance",
  HR_EMPLOYEE_MGMT: "hr_employee_mgmt",
  HR_SHIFTS: "shifts",
  HR_LEAVE_REQUESTS: "hr_leave_requests",
  HR_LEAVE_APPROVALS: "hr_leave_approvals",
  HR_LEAVE_TYPES: "hr_leave_types",
  HR_HOLIDAYS: "holidays",
  HR_PERFORMANCE: "hr_performance",
  HR_OKRS: "okrs",
  HR_GOALS: "goals",
  HR_EMPLOYEES: "employees",
  HR_DEPARTMENTS: "departments",
  HR_TEAMS: "teams",
  HR_ROLES: "roles",
  HR_ONBOARDING: "onboarding",
  HR_OFFBOARDING: "offboarding",
  HR_REQUISITIONS: "requisitions",
  HR_JOB_OPENINGS: "job_openings",
  HR_CANDIDATES: "candidates",
  HR_CAMPAIGNS: "hr_campaigns",
  CALL_CAMPAIGNS: "call_campaigns",
  VOICE_AGENTS: "voice_agents",
  VOICE_AGENT_CAMPAIGNS: "voice_agent_campaigns",
  VOICE_AGENT_CALL_LOGS: "voice_agent_call_logs",
};

export const POLICY_ACTIONS = {
  CREATE: "CREATE",
  VIEW: "VIEW",
  EDIT: "EDIT",
  DELETE: "DELETE",
  EXPORT: "EXPORT",
  IMPORT: "IMPORT",
  ENABLED: "ENABLED",
};

export const NAMESPACE_CODEBASE = "CODEBASE";

export const CODEBASE_ACTIONS = {
  VIEW: "VIEW",
  EDIT: "EDIT",
  MANAGE: "MANAGE",
};

export const CODEBASE_ACTION_OPTIONS = [
  { value: CODEBASE_ACTIONS.VIEW, label: "Read", description: "(clone, browse)" },
  { value: CODEBASE_ACTIONS.EDIT, label: "Write", description: "(read + push)" },
  { value: CODEBASE_ACTIONS.MANAGE, label: "Manage", description: "(write + settings, branch rules)" },
];

// Priority order for resolving the highest-privilege codebase action (MANAGE > EDIT > VIEW).
export const CODEBASE_ACTION_PRIORITY = [
  CODEBASE_ACTIONS.MANAGE,
  CODEBASE_ACTIONS.EDIT,
  CODEBASE_ACTIONS.VIEW,
];

export const POLICY_SCOPES = {
  ALL: "all", // Internal UI value for "All records"
  NONE: "none", // Internal UI value for "None"
  CONDITIONAL: "conditional", // Internal UI value for conditional access
  OWN: "own", // Internal UI value for user's own records
  TEAM: "team", // Internal UI value for team records
  // Backend enum values (for reference)
  ALL_RECORDS: "ALL_RECORDS",
  NONE_OF_THE_RECORDS: "NONE_OF_THE_RECORDS",
  USER: "USER",
  TEAM_BACKEND: "TEAM",
  CONDITIONAL_BACKEND: "CONDITIONAL",
};

// Conditional access types
export const CONDITION_TYPES = {
  // UI values
  USER_IN_ROLE: "user_in_role",
  USER_AND_TEAM: "user_and_team",
  SPECIFIC_USERS_TEAMS: "specific_users_teams",
  // Backend values
  USER_SCOPE: "USER_SCOPE",
  TEAM_SCOPE: "TEAM_SCOPE",
  SELECTED_ENTITIES_SCOPE: "SELECTED_ENTITIES_SCOPE",
};

export const getCRMScopeOptions = (objectName) => [
  {
    value: POLICY_SCOPES.NONE,
    label: `None of the ${objectName}`,
  },
  {
    value: POLICY_SCOPES.ALL,
    label: `All ${objectName}`,
  },
  {
    value: POLICY_SCOPES.CONDITIONAL,
    label: "Conditional",
  },
];

export const getPermissionSectionsConfig = (featureNames) => ({
  settings: {
    name: "Settings",
    iconName: "SettingOutlined",
    permissions: [
      {
        key: permissionConstants.USERS,
        name: "Account Access",
        description:
          "Manage inviting users, creating teams and creating permission sets",
      },
      {
        key: permissionConstants.BILLING,
        name: "Modify Billing",
        description: "Manage billing plans and view credit consumption",
      },
      {
        key: permissionConstants.INTEGRATIONS,
        name: "Manage Integration",
        description: "Manage billing plans and view credit consumption",
      },
      {
        key: permissionConstants.CALL_SETTINGS,
        name: "Call settings",
        description: "Manage call settings",
      },
      {
        key: permissionConstants.SHARED_EMAILS,
        name: "Shared Emails",
        description: "Shared email settings",
      },
      {
        key: permissionConstants.SHARED_LINKEDIN,
        name: "Shared Linkedin",
        description: "Shared linkedin settings",
      },
      {
        key: permissionConstants.OBJECTS,
        name: "Custom Fields",
        description: "Custom Fields settings",
      },
      {
        key: permissionConstants.DEAL_PIPELINE,
        name: "Deal Pipeline",
        description: "Manage deal pipeline settings",
      },
      {
        key: permissionConstants.IMPORT_ACCESS,
        name: "Import Access",
        description: "Can import CSV of any object into SuperAGI",
      },
      {
        key: permissionConstants.EXPORT_ACCESS,
        name: "Export Access",
        description: "Can export any object data from SuperAGI",
      },
      {
        key: permissionConstants.CUSTOM_VIEWS,
        name: "Custom Views",
        description:
          "Can create and save custom views for all objects for everyone",
      },
      {
        key: permissionConstants.BULK_DELETE,
        name: "Bulk Delete",
        description: "Can delete multiple records at once",
      },
    ],
  },
  prospect: {
    name: "Prospect",
    iconName: "SearchOutlined",
    permissions: [
      {
        key: permissionConstants.PROSPECT_LEADS,
        name: "Prospect Leads",
        description: "Prospect leads settings",
      },
      {
        key: permissionConstants.PROSPECT_COMPANIES,
        name: "Prospect Companies",
        description: "Prospect companies settings",
      },
    ],
  },
  engage: {
    name: "Engage",
    iconName: "PlayCircleOutlined",
    permissions: [
      {
        key: permissionConstants.AI_SDR,
        name: featureNames.COLD_OUTREACH,
        description: "Setup campaigns and monitor performance",
      },
      {
        key: permissionConstants.SEQUENCES,
        name: featureNames.SEQUENCES,
        description: `Setup ${featureNames.SEQUENCES.toLowerCase()}s and monitor performance`,
      },
      {
        key: permissionConstants.DIALER,
        name: "Dialer",
        description:
          "Setup phone numbers, campaigns, voicemails, call forwarding and call availability",
      },
      {
        key: permissionConstants.CONVERSATIONS,
        name: "Conversations",
        description: "Access to all conversation features",
      },
      {
        key: permissionConstants.MEETINGS,
        name: "Meetings",
        description: "Setup meeting agents and round-robin availability",
      },
      {
        key: permissionConstants.EMAILS,
        name: "Emails",
        description: "View sent emails and track their performance metrics",
        hasExtra: true,
        extraInputs: [
          {
            key: "emails_view",
            type: "select",
            label: "View",
            options: [
              {
                value: "team_emails",
                label: "Emails from personal and shared mailboxes",
              },
              { value: "all_emails", label: "All Emails" },
            ],
            default: "team_emails",
          },
        ],
      },
    ],
  },
  crm: {
    name: "CRM",
    iconName: "DatabaseOutlined",
    permissions: [],
  },
  analytics: {
    name: "Analytics",
    iconName: "LineChartOutlined",
    permissions: [
      {
        key: permissionConstants.ANALYTICS,
        name: "Analytics",
        description: "Access to view for analytics and reports",
      },
    ],
  },
  automate: {
    name: "Automate",
    iconName: "ThunderboltOutlined",
    permissions: [
      {
        key: permissionConstants.WORKFLOWS,
        name: featureNames.WORKFLOWS,
        description: `Manage ${featureNames.WORKFLOWS.toLowerCase()}`,
      },
      {
        key: permissionConstants.TASKS,
        name: "Tasks",
        description: "Access to view, edit and delete self owned tasks",
      },
      {
        key: permissionConstants.ALL_TASKS,
        name: "All Tasks",
        description: "Access to view, edit and delete all tasks",
      },
    ],
  },
});
